import React, { useState, useEffect } from 'react';
import {
    MonitorCog, SquareActivity, Tickets, Menu, X, Home, Package, FileText, Calendar, Users, BarChart3, Settings,
    Bell, Search, ChevronRight, Building, LogOut, RefreshCw, Save, RotateCcw,
    Download, Shield, Database, Palette, User, Lock, Mail, Phone, MapPin,
    CheckCircle, AlertCircle, Activity, Globe, Clock, Key, AlertTriangle,
    Cable, UserCog, Construction, Beaker, Leaf, ShieldCheck, Wrench,Receipt , Briefcase,
    Hammer, Calculator, Sun, Moon, Laptop, Zap, Folder
} from 'lucide-react';
import '../Styles/products.css';
import { useNavigate } from "react-router-dom";
import APIService from "../services/api";
import MepIcon from '../assets/MEP_Solutions.png';
import architecturalIcon from '../assets/architectural_icon.png';
import civilIcon from '../assets/civil-icon.png';
import constructionIcon from '../assets/construction-icon.png';
import powerToolsIcon from '../assets/power-tools_icon.png';
import sustainableIcon from '../assets/sustainable-icon.png';
import hygienicIcon from '../assets/hygienic-icon.png';
import '../Styles/dashboard.css';

const UnitechSettingsPage = () => {
    const [user, setUser] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [activeMenuItem, setActiveMenuItem] = useState('settings');
    const [currentTime, setCurrentTime] = useState(new Date());
    const [notifications, setNotifications] = useState(3);
    const [activeTab, setActiveTab] = useState('general');
    const [isLoading, setIsLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isAuthLoading, setIsAuthLoading] = useState(true);
    const [isSettingsLoading, setIsSettingsLoading] = useState(true);
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [currentTheme, setCurrentTheme] = useState('light');
    const [showMaintenancePasswordModal, setShowMaintenancePasswordModal] = useState(false);
    const [maintenancePassword, setMaintenancePassword] = useState('');
    const [pendingMaintenanceState, setPendingMaintenanceState] = useState(false);

    const [settings, setSettings] = useState({
        // Company Information
        companyName: 'UNITECH IKK',
        companyEmail: 'contact@unitech-ikk.com',
        companyPhone: '+961 1 234 567',
        companyAddress: 'Beirut, Lebanon',
        timezone: 'Asia/Beirut',
        language: 'fr',
        currency: 'EUR',

        // Notifications
        emailNotifications: true,
        smsNotifications: false,
        stockAlerts: true,
        lowStockThreshold: 10,
        orderNotifications: true,

        // Security
        twoFactorAuth: false,
        sessionTimeout: 60,
        passwordExpiry: 90,
        loginAttempts: 5,

        // System
        autoBackup: true,
        backupFrequency: 'daily',
        maintenanceMode: false,
        debugMode: false,

        // Appearance
        theme: 'light',
        compactMode: false,
        showAnimations: true,
        sidebarCollapsed: false,

        // Advanced
        maxFileUploadSize: 10,
        defaultItemsPerPage: 20,
        enableAuditLog: true,
        enableApiAccess: false,
        apiRateLimit: 100,
        allowGuestAccess: false,
        defaultUserRole: 'user',
        passwordMinLength: 8,
        requirePasswordSpecialChars: true,
        dataRetentionDays: 365,
        enableEmailVerification: true,
        enableSMSVerification: false,
        googleAnalyticsId: '',
        enableChatSupport: true,
        workingHours: {
            start: '09:00',
            end: '17:00'
        },
        workingDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],

        // System Info
        version: 'v2.1.3',
        status: 'online',
        lastBackup: new Date()
    });

    const [originalSettings, setOriginalSettings] = useState({});
    const [hasChanges, setHasChanges] = useState(false);

    const handleRefresh = () => {
        loadSettings();
    };

    useEffect(() => {
        checkAuthStatus();
    }, []);

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        if (user) {
            loadSettings();
        }
    }, [user]);

    useEffect(() => {
        if (Object.keys(originalSettings).length > 0) {
            const hasChanges = JSON.stringify(settings) !== JSON.stringify(originalSettings);
            setHasChanges(hasChanges);
        }
    }, [settings, originalSettings]);

    // Theme switching effect
    useEffect(() => {
        const theme = settings.theme || 'light';
        setCurrentTheme(theme);

        // Apply theme to document
        if (theme === 'dark') {
            document.documentElement.classList.add('dark-theme');
            document.documentElement.classList.remove('light-theme');
        } else if (theme === 'light') {
            document.documentElement.classList.add('light-theme');
            document.documentElement.classList.remove('dark-theme');
        } else { // auto
            const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            if (prefersDark) {
                document.documentElement.classList.add('dark-theme');
                document.documentElement.classList.remove('light-theme');
            } else {
                document.documentElement.classList.add('light-theme');
                document.documentElement.classList.remove('dark-theme');
            }
        }
    }, [settings.theme]);

    const checkAuthStatus = async () => {
        try {
            const response = await fetch('https://utk-intra-api.unitech-ikk.com/api/auth/me', {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('unitech_token')}`
                }
            });

            const data = await response.json();

            if (response.ok && data.success && data.user) {
                setUser(data.user);
                if (data.user.role !== 'admin') {
                    navigate('/dashboard');
                    return;
                }
            } else {
                window.location.href = '/login';
            }
        } catch (error) {
            console.error('Erreur de vérification auth:', error);
            window.location.href = '/login';
        } finally {
            setIsAuthLoading(false);
        }
    };

    const loadSettings = async () => {
        try {
            setIsSettingsLoading(true);
            const response = await fetch('https://utk-intra-api.unitech-ikk.com/api/settings', {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('unitech_token')}`
                }
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setSettings(data.settings);
                setOriginalSettings(JSON.parse(JSON.stringify(data.settings)));
            } else {
                showErrorMessage('Erreur lors du chargement des paramètres');
            }
        } catch (error) {
            console.error('Erreur lors du chargement des paramètres:', error);
            showErrorMessage('Erreur de connexion au serveur');
        } finally {
            setIsSettingsLoading(false);
        }
    };

    const handleSaveSettings = async () => {
        if (!hasChanges) {
            showSuccessMessage('Aucune modification à sauvegarder');
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch('https://utk-intra-api.unitech-ikk.com/api/settings', {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('unitech_token')}`
                },
                body: JSON.stringify(settings)
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setSettings(data.settings);
                setOriginalSettings(JSON.parse(JSON.stringify(data.settings)));
                showSuccessMessage('Paramètres sauvegardés avec succès !');
            } else {
                showErrorMessage(data.message || 'Erreur lors de la sauvegarde');
            }
        } catch (error) {
            console.error('Erreur de sauvegarde:', error);
            showErrorMessage('Erreur de connexion au serveur');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetSettings = async () => {
        if (!window.confirm('Êtes-vous sûr de vouloir réinitialiser tous les paramètres aux valeurs par défaut ?')) {
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch('https://utk-intra-api.unitech-ikk.com/api/settings/reset', {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('unitech_token')}`
                }
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setSettings(data.settings);
                setOriginalSettings(JSON.parse(JSON.stringify(data.settings)));
                showSuccessMessage('Paramètres réinitialisés aux valeurs par défaut');
            } else {
                showErrorMessage(data.message || 'Erreur lors de la réinitialisation');
            }
        } catch (error) {
            console.error('Erreur de réinitialisation:', error);
            showErrorMessage('Erreur de connexion au serveur');
        } finally {
            setIsLoading(false);
        }
    };

    const handleExportSettings = async () => {
        try {
            const response = await fetch('https://utk-intra-api.unitech-ikk.com/api/settings/export', {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('unitech_token')}`
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `unitech-settings-${new Date().toISOString().split('T')[0]}.json`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                showSuccessMessage('Configuration exportée avec succès');
            } else {
                showErrorMessage('Erreur lors de l\'export');
            }
        } catch (error) {
            console.error('Erreur d\'export:', error);
            showErrorMessage('Erreur de connexion au serveur');
        }
    };

    const handleInputChange = (field, value) => {
        setSettings(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleMaintenanceToggle = (newState) => {
        if (newState && !settings.maintenanceMode) {
            // Turning ON maintenance mode - require password
            setPendingMaintenanceState(true);
            setShowMaintenancePasswordModal(true);
        } else if (!newState && settings.maintenanceMode) {
            // Turning OFF maintenance mode - require password
            setPendingMaintenanceState(false);
            setShowMaintenancePasswordModal(true);
        }
    };

    const handleMaintenancePasswordSubmit = async () => {
        // You should validate this password on the backend
        if (maintenancePassword === 'UNITECH2024!') {
            setSettings(prev => ({
                ...prev,
                maintenanceMode: pendingMaintenanceState
            }));
            setShowMaintenancePasswordModal(false);
            setMaintenancePassword('');
            showSuccessMessage(`Mode maintenance ${pendingMaintenanceState ? 'activé' : 'désactivé'}`);
        } else {
            showErrorMessage('Mot de passe incorrect');
        }
    };

    const handleLogout = async () => {
        if (window.confirm('Are you sure you wanna disconnect?')) {
            // Nettoyer les données utilisateur
            APIService.clearUserData();

            // Utiliser React Router pour naviguer vers login
            navigate('/login');
        }
    };

    const showSuccessMessage = (message) => {
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
    };

    const showErrorMessage = (message) => {
        setErrorMessage(message);
        setShowError(true);
        setTimeout(() => setShowError(false), 5000);
    };

    const menuItems = [
        {
            id: 'barcoding',
            label: 'Barcoding Database',
            icon: FileText, // Keep as Lucide icon
            path: '/barcoding'
        },
        {
            id: 'mep-solutions',
            label: 'MEP Solutions',
            icon: MepIcon, // 🔄 Replace with image
            isImage: true,
            path: '/mep_solutions'
        },
        {
            id: 'architectural-solutions',
            label: 'Architectural Solutions',
            icon: architecturalIcon , // 🔄 Replace with image
            isImage: true,
            path: '/architectural_solutions'
        },
        {
            id: 'civil-solutions',
            label: 'Civil Solutions',
            icon: civilIcon, // 🔄 Replace with image
            isImage: true,
            path: '/civil_solutions'
        },
        {
            id: 'construction-chemicals',
            label: 'Construction Chemicals',
            icon: constructionIcon, // 🔄 Replace with image
            isImage: true,
            path: '/construction_chemicals'
        },
        {
            id: 'power-tools',
            label: 'Power & Industrial Tools',
            icon: powerToolsIcon, // 🔄 Replace with image
            isImage: true,
            path: '/power_tools'
        },
        {
            id: 'sustainable-solutions',
            label: 'Sustainable Solutions',
            icon: sustainableIcon, // 🔄 Replace with image
            isImage: true,
            path: '/sustainable_solutions'
        },
        {
            id: 'hygienic-solutions',
            label: 'Hygienic Solutions',
            icon: hygienicIcon, // 🔄 Replace with image
            isImage: true,
            path: '/hygienic_solutions'
        },
        {
            id: 'conversions',
            label: 'Engineering Conversions',
            icon: Calculator, // Keep as Lucide icon
            path: '/conversion'
        },
        {
            id: 'analytics',
            label: 'Amazon Products',
            icon: BarChart3, // Keep as Lucide icon
            path: '/amazon_products'
        },
        {
            id: 'team',
            label: 'Team Management',
            icon: Users, // Keep as Lucide icon
            path: '/team_management'
        },
        {
            id: 'user-management',
            label: 'User Management',
            icon: UserCog, // Keep as Lucide icon
            path: '/users_management'
        },
    ];

    const filteredMenuItems = menuItems.filter(item => {
        if (item.id === 'team' && user?.role === 'user') return false;
        if (item.id === 'settings' && user?.role !== 'admin') return false;
        if (item.id === 'user-management' && user?.role !== 'admin') return false;
        return true;
    });

    const getUserInitials = () => {
        if (!user) return 'U';
        const firstInitial = user.firstName ? user.firstName.charAt(0).toUpperCase() : '';
        const lastInitial = user.lastName ? user.lastName.charAt(0).toUpperCase() : '';
        return firstInitial + lastInitial || user.email.charAt(0).toUpperCase();
    };

    const getDisplayName = () => {
        if (!user) return 'Utilisateur';
        if (user.firstName && user.lastName) {
            return `${user.firstName} ${user.lastName}`;
        }
        return user.email;
    };

    if (isAuthLoading && !user) {
        return (
            <div className="loading-screen">
                <div className="loading-content">
                    <div className="loading-spinner"></div>
                    <p className="loading-text">Chargement de UNITECH IKK...</p>
                </div>
            </div>
        );
    }

    if (!user) {
        return null;
    }

    const tabs = [
        { id: 'general', label: 'Général', icon: Settings },
        { id: 'notifications', label: 'Notifications', icon: Bell },
        { id: 'security', label: 'Sécurité', icon: Shield },
        { id: 'system', label: 'Système', icon: Database },
        { id: 'advanced', label: 'Avancé', icon: Zap },
        { id: 'appearance', label: 'Apparence', icon: Palette }
    ];

    const ToggleSwitch = ({ checked, onChange, color = '#059669' }) => (
        <label style={{ position: 'relative', display: 'inline-block', width: '60px', height: '34px' }}>
            <input
                type="checkbox"
                checked={checked}
                onChange={onChange}
                style={{ opacity: 0, width: 0, height: 0 }}
            />
            <span style={{
                position: 'absolute',
                cursor: 'pointer',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: checked ? color : '#ccc',
                transition: '.4s',
                borderRadius: '34px'
            }}>
                <span style={{
                    position: 'absolute',
                    content: '',
                    height: '26px',
                    width: '26px',
                    left: checked ? '30px' : '4px',
                    bottom: '4px',
                    backgroundColor: 'white',
                    transition: '.4s',
                    borderRadius: '50%'
                }} />
            </span>
        </label>
    );

    return (
        <div className="dashboard-container">
            {/* Success Notification */}
            {showSuccess && (
                <div style={{
                    position: 'fixed',
                    top: '20px',
                    right: '20px',
                    background: '#10b981',
                    color: 'white',
                    padding: '12px 20px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    zIndex: 1000,
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                }}>
                    <CheckCircle style={{ width: '20px', height: '20px' }} />
                    Paramètres sauvegardés avec succès !
                </div>
            )}

            {/* Error Notification */}
            {showError && (
                <div style={{
                    position: 'fixed',
                    top: '20px',
                    right: '20px',
                    background: '#ef4444',
                    color: 'white',
                    padding: '12px 20px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    zIndex: 1000,
                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
                }}>
                    <AlertCircle style={{ width: '20px', height: '20px' }} />
                    {errorMessage}
                </div>
            )}

            {/* Maintenance Password Modal */}
            {showMaintenancePasswordModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        background: 'white',
                        padding: '24px',
                        borderRadius: '12px',
                        width: '400px',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                    }}>
                        <h3 style={{ margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Lock style={{ width: '20px', height: '20px', color: '#dc2626' }} />
                            {pendingMaintenanceState ? 'Activer' : 'Désactiver'} le mode maintenance
                        </h3>
                        <p style={{ margin: '0 0 16px 0', color: '#6b7280' }}>
                            Entrez le mot de passe administrateur pour continuer
                        </p>
                        <input
                            type="password"
                            placeholder="Mot de passe administrateur"
                            value={maintenancePassword}
                            onChange={(e) => setMaintenancePassword(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleMaintenancePasswordSubmit()}
                            style={{
                                width: '100%',
                                padding: '12px',
                                border: '1px solid #d1d5db',
                                borderRadius: '8px',
                                fontSize: '14px',
                                boxSizing: 'border-box',
                                marginBottom: '16px'
                            }}
                        />
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => {
                                    setShowMaintenancePasswordModal(false);
                                    setMaintenancePassword('');
                                }}
                                style={{
                                    padding: '8px 16px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    background: 'white',
                                    cursor: 'pointer'
                                }}
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleMaintenancePasswordSubmit}
                                style={{
                                    padding: '8px 16px',
                                    border: 'none',
                                    borderRadius: '6px',
                                    background: '#dc2626',
                                    color: 'white',
                                    cursor: 'pointer'
                                }}
                            >
                                Confirmer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Sidebar */}
            <aside className="sidebar">
                <div className="sidebar-header">
                    <div
                        className="sidebar-logo-container"
                        onClick={() => navigate('/dashboard')}
                        style={{
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            borderRadius: '8px',
                            padding: '8px'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                            e.currentTarget.style.transform = 'scale(1.02)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.transform = 'scale(1)';
                        }}
                        title="Retour au Dashboard"
                    >
                        <div className="sidebar-logo">
                            <Building style={{ width: '24px', height: '24px', color: 'white' }} />
                        </div>
                        <div className="sidebar-brand">
                            <h1 className="sidebar-title">UNITECH IKK</h1>
                            <p className="sidebar-subtitle">Enterprise Dashboard</p>
                        </div>
                    </div>
                </div>
                <nav className="sidebar-nav">
                    {filteredMenuItems.map((item) => (
                        <div
                            key={item.id}
                            className={`nav-item ${activeMenuItem === item.id ? 'active' : ''}`}
                            onClick={() => {
                                setActiveMenuItem(item.id);
                                navigate(item.path);
                            }}
                        >
                            {item.isImage ? (
                                <img
                                    src={item.icon}
                                    alt={item.label}
                                    className="nav-icon-image"
                                    style={{
                                        width: '20px',
                                        height: '20px',
                                        display: 'block' // Ensures proper alignment
                                    }}
                                />
                            ) : (
                                <item.icon
                                    style={{
                                        width: '20px',
                                        height: '20px',
                                        display: 'block' // Ensures consistency with images
                                    }}
                                />
                            )}
                            <span className="nav-text">{item.label}</span>
                        </div>
                    ))}
                </nav>
                <div className="sidebar-footer">
                    <div className="user-profile">
                        <div className="user-avatar">{getUserInitials()}</div>
                        <div className="user-info">
                            <p className="user-name">{getDisplayName()}</p>
                            <p className="user-role">{user?.role || 'Utilisateur'}</p>
                        </div>
                        <button
                            className="logout-button"
                            onClick={handleLogout}
                            title="Se déconnecter"
                        >
                            <LogOut style={{ width: '16px', height: '16px' }} />
                        </button>
                    </div>
                </div>
            </aside>

            {/* Main Content */}
            <main className="main-content">
                {/* Header */}
                <header className="header">
                    <div className="header-content">
                        <div className="header-left">
                            <button
                                className="menu-toggle"
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                            >
                                {sidebarOpen ?
                                    <X style={{ width: '20px', height: '20px' }} /> :
                                    <Menu style={{ width: '20px', height: '20px' }} />
                                }
                            </button>

                            <nav className="breadcrumb">
                                <div className="breadcrumb-item">
                                    <Home style={{ width: '16px', height: '16px' }} />
                                    <span>Dashboard</span>
                                </div>
                                <ChevronRight style={{ width: '14px', height: '14px' }} />
                                <div className="breadcrumb-item">
                                    <Settings style={{ width: '16px', height: '16px' }} />
                                    <span>Settings</span>
                                </div>
                                <ChevronRight style={{ width: '14px', height: '14px' }} />
                                <div className="breadcrumb-item">
                                    <span>Configuration</span>
                                </div>
                            </nav>
                        </div>

                        <div className="header-right">
                            <div className="search-container">
                                <input
                                    type="text"
                                    className="search-input"
                                    placeholder="Rechercher..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <Search className="search-icon" style={{ width: '18px', height: '18px' }} />
                            </div>

                            <div className="header-actions">
                                <button
                                    className="header-button"
                                    onClick={handleRefresh}
                                    disabled={isLoading}
                                    title="Actualiser"
                                >
                                    <RefreshCw style={{
                                        width: '18px',
                                        height: '18px',
                                        transform: isLoading ? 'rotate(360deg)' : 'none',
                                        transition: 'transform 1s linear'
                                    }} />
                                </button>

                                <button
                                    className="header-button"
                                    title="Notifications"
                                >
                                    <Bell style={{ width: '18px', height: '18px' }} />
                                    {notifications.length > 0 && (
                                        <span className="notification-badge">{notifications.length}</span>
                                    )}
                                </button>

                                <button
                                    className="header-button"
                                    onClick={() => navigate('/settings')}
                                    title="Paramètres système"
                                >
                                    <Settings style={{ width: '18px', height: '18px' }} />
                                </button>

                                <button
                                    className="header-button"
                                    onClick={() => navigate('/project_management')}
                                    title="Gestion de projets"
                                >
                                    <Folder style={{ width: '18px', height: '18px' }} />
                                </button>

                                <button
                                    className="header-button"
                                    onClick={() => navigate('/technical_support')}
                                    title="Technical Support"
                                >
                                    <MonitorCog style={{ width: '18px', height: '18px' }} />
                                </button>

                                <button
                                    className="header-button"
                                    onClick={() => navigate('/admin_support_it')}
                                    title="Admin Support IT"
                                >
                                    <SquareActivity style={{ width: '18px', height: '18px' }} />
                                </button>

                                <button
                                    className="header-button"
                                    onClick={() => navigate('/mytickets')}
                                    title="My Tickets"
                                >
                                    <Tickets style={{ width: '18px', height: '18px' }} />
                                </button>

                                <button
                                    className="header-button"
                                    onClick={() => navigate('/quotations')}
                                    title="Quotation System"
                                >
                                    <Receipt style={{ width: '18px', height: '18px' }} />
                                </button>

                                <button
                                    className="header-button"
                                    onClick={() => navigate('/clients')}
                                    title="Clients"
                                >
                                    <Briefcase style={{ width: '18px', height: '18px' }} />
                                </button>

                                <div className="time-display">
                                    {currentTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                </div>
                            </div>
                        </div>
                    </div>
                </header>

                {/* Settings Content */}
                <div className="products-content">
                    {/* Page Header */}
                    <div className="page-header">
                        <h1 className="page-title">Paramètres Système ⚙️</h1>
                        <p className="page-subtitle">
                            Configurez et personnalisez votre plateforme UNITECH IKK.
                        </p>

                        <div className="page-actions">
                            <button
                                className="action-button"
                                onClick={handleSaveSettings}
                                disabled={isLoading || !hasChanges}
                                style={{
                                    cursor: (isLoading || !hasChanges) ? 'not-allowed' : 'pointer',
                                    opacity: (isLoading || !hasChanges) ? 0.7 : 1,
                                    backgroundColor: hasChanges ? '#059669' : '#6b7280'
                                }}
                            >
                                {isLoading ? (
                                    <>
                                        <RefreshCw style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} />
                                        Sauvegarde...
                                    </>
                                ) : (
                                    <>
                                        <Save style={{ width: '16px', height: '16px' }} />
                                        {hasChanges ? 'Sauvegarder les modifications' : 'Aucune modification'}
                                    </>
                                )}
                            </button>
                            <button
                                className="action-button secondary"
                                onClick={handleResetSettings}
                                disabled={isLoading}
                            >
                                <RotateCcw style={{ width: '16px', height: '16px' }} />
                                Réinitialiser
                            </button>
                            <button
                                className="action-button secondary"
                                onClick={handleExportSettings}
                                disabled={isLoading}
                            >
                                <Download style={{ width: '16px', height: '16px' }} />
                                Exporter Config
                            </button>
                        </div>
                    </div>

                    {/* Loading State */}
                    {isSettingsLoading ? (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '400px',
                            flexDirection: 'column',
                            gap: '20px'
                        }}>
                            <div className="loading-spinner"></div>
                            <p>Chargement des paramètres...</p>
                        </div>
                    ) : (
                        <>
                            {/* Settings Tabs */}
                            <div style={{ marginBottom: '30px' }}>
                                <div style={{
                                    display: 'flex',
                                    borderBottom: '2px solid #e5e7eb',
                                    marginBottom: '30px'
                                }}>
                                    {tabs.map((tab) => (
                                        <button
                                            key={tab.id}
                                            onClick={() => setActiveTab(tab.id)}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                padding: '12px 20px',
                                                border: 'none',
                                                background: 'none',
                                                color: activeTab === tab.id ? '#1e40af' : '#6b7280',
                                                borderBottom: activeTab === tab.id ? '2px solid #1e40af' : '2px solid transparent',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                fontWeight: activeTab === tab.id ? '600' : '500',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            <tab.icon style={{ width: '18px', height: '18px' }} />
                                            {tab.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Tab Content */}
                            <div style={{ display: 'grid', gap: '24px' }}>
                                {/* General Tab */}
                                {activeTab === 'general' && (
                                    <div className="category-card" style={{ padding: '24px' }}>
                                        <h3 style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            margin: '0 0 20px 0',
                                            fontSize: '18px',
                                            fontWeight: '600'
                                        }}>
                                            <Building style={{ width: '20px', height: '20px', color: '#1e40af' }} />
                                            Informations de l'entreprise
                                        </h3>

                                        <div style={{ display: 'grid', gap: '20px' }}>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                                <div>
                                                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                                                        Nom de l'entreprise
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={settings.companyName || ''}
                                                        onChange={(e) => handleInputChange('companyName', e.target.value)}
                                                        style={{
                                                            width: '100%',
                                                            padding: '12px',
                                                            border: '1px solid #d1d5db',
                                                            borderRadius: '8px',
                                                            fontSize: '14px',
                                                            boxSizing: 'border-box'
                                                        }}
                                                    />
                                                </div>

                                                <div>
                                                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                                                        Email
                                                    </label>
                                                    <input
                                                        type="email"
                                                        value={settings.companyEmail || ''}
                                                        onChange={(e) => handleInputChange('companyEmail', e.target.value)}
                                                        style={{
                                                            width: '100%',
                                                            padding: '12px',
                                                            border: '1px solid #d1d5db',
                                                            borderRadius: '8px',
                                                            fontSize: '14px',
                                                            boxSizing: 'border-box'
                                                        }}
                                                    />
                                                </div>
                                            </div>

                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                                <div>
                                                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                                                        Téléphone
                                                    </label>
                                                    <input
                                                        type="tel"
                                                        value={settings.companyPhone || ''}
                                                        onChange={(e) => handleInputChange('companyPhone', e.target.value)}
                                                        style={{
                                                            width: '100%',
                                                            padding: '12px',
                                                            border: '1px solid #d1d5db',
                                                            borderRadius: '8px',
                                                            fontSize: '14px',
                                                            boxSizing: 'border-box'
                                                        }}
                                                    />
                                                </div>

                                                <div>
                                                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                                                        Fuseau horaire
                                                    </label>
                                                    <select
                                                        value={settings.timezone || 'Asia/Beirut'}
                                                        onChange={(e) => handleInputChange('timezone', e.target.value)}
                                                        style={{
                                                            width: '100%',
                                                            padding: '12px',
                                                            border: '1px solid #d1d5db',
                                                            borderRadius: '8px',
                                                            fontSize: '14px',
                                                            boxSizing: 'border-box'
                                                        }}
                                                    >
                                                        <option value="Asia/Beirut">Asia/Beirut (GMT+3)</option>
                                                        <option value="Europe/Paris">Europe/Paris (GMT+1)</option>
                                                        <option value="America/New_York">America/New_York (GMT-5)</option>
                                                        <option value="Asia/Dubai">Asia/Dubai (GMT+4)</option>
                                                    </select>
                                                </div>
                                            </div>

                                            <div>
                                                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                                                    Adresse
                                                </label>
                                                <textarea
                                                    value={settings.companyAddress || ''}
                                                    onChange={(e) => handleInputChange('companyAddress', e.target.value)}
                                                    rows="3"
                                                    style={{
                                                        width: '100%',
                                                        padding: '12px',
                                                        border: '1px solid #d1d5db',
                                                        borderRadius: '8px',
                                                        fontSize: '14px',
                                                        boxSizing: 'border-box',
                                                        resize: 'vertical'
                                                    }}
                                                />
                                            </div>

                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                                <div>
                                                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                                                        Langue
                                                    </label>
                                                    <select
                                                        value={settings.language || 'fr'}
                                                        onChange={(e) => handleInputChange('language', e.target.value)}
                                                        style={{
                                                            width: '100%',
                                                            padding: '12px',
                                                            border: '1px solid #d1d5db',
                                                            borderRadius: '8px',
                                                            fontSize: '14px',
                                                            boxSizing: 'border-box'
                                                        }}
                                                    >
                                                        <option value="fr">Français</option>
                                                        <option value="en">English</option>
                                                        <option value="ar">العربية</option>
                                                    </select>
                                                </div>

                                                <div>
                                                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                                                        Devise
                                                    </label>
                                                    <select
                                                        value={settings.currency || 'EUR'}
                                                        onChange={(e) => handleInputChange('currency', e.target.value)}
                                                        style={{
                                                            width: '100%',
                                                            padding: '12px',
                                                            border: '1px solid #d1d5db',
                                                            borderRadius: '8px',
                                                            fontSize: '14px',
                                                            boxSizing: 'border-box'
                                                        }}
                                                    >
                                                        <option value="EUR">Euro (€)</option>
                                                        <option value="USD">US Dollar ($)</option>
                                                        <option value="LBP">Lebanese Pound (ل.ل)</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Notifications Tab */}
                                {activeTab === 'notifications' && (
                                    <div className="category-card" style={{ padding: '24px' }}>
                                        <h3 style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            margin: '0 0 20px 0',
                                            fontSize: '18px',
                                            fontWeight: '600'
                                        }}>
                                            <Bell style={{ width: '20px', height: '20px', color: '#059669' }} />
                                            Préférences de notification
                                        </h3>

                                        <div style={{ display: 'grid', gap: '24px' }}>
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '16px',
                                                background: '#f9fafb',
                                                borderRadius: '8px'
                                            }}>
                                                <div>
                                                    <h4 style={{ margin: '0 0 4px 0', fontSize: '16px' }}>Notifications par email</h4>
                                                    <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                                                        Recevoir les notifications importantes par email
                                                    </p>
                                                </div>
                                                <ToggleSwitch
                                                    checked={settings.emailNotifications || false}
                                                    onChange={(e) => handleInputChange('emailNotifications', e.target.checked)}
                                                />
                                            </div>

                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '16px',
                                                background: '#f9fafb',
                                                borderRadius: '8px'
                                            }}>
                                                <div>
                                                    <h4 style={{ margin: '0 0 4px 0', fontSize: '16px' }}>Notifications SMS</h4>
                                                    <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                                                        Recevoir les alertes critiques par SMS
                                                    </p>
                                                </div>
                                                <ToggleSwitch
                                                    checked={settings.smsNotifications || false}
                                                    onChange={(e) => handleInputChange('smsNotifications', e.target.checked)}
                                                />
                                            </div>

                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '16px',
                                                background: '#f9fafb',
                                                borderRadius: '8px'
                                            }}>
                                                <div>
                                                    <h4 style={{ margin: '0 0 4px 0', fontSize: '16px' }}>Alertes de stock</h4>
                                                    <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                                                        Notifications quand le stock est faible
                                                    </p>
                                                </div>
                                                <ToggleSwitch
                                                    checked={settings.stockAlerts || false}
                                                    onChange={(e) => handleInputChange('stockAlerts', e.target.checked)}
                                                />
                                            </div>

                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                                <div>
                                                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                                                        Seuil stock faible
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        max="100"
                                                        value={settings.lowStockThreshold || 10}
                                                        onChange={(e) => handleInputChange('lowStockThreshold', parseInt(e.target.value))}
                                                        style={{
                                                            width: '100%',
                                                            padding: '12px',
                                                            border: '1px solid #d1d5db',
                                                            borderRadius: '8px',
                                                            fontSize: '14px',
                                                            boxSizing: 'border-box'
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Security Tab */}
                                {activeTab === 'security' && (
                                    <div className="category-card" style={{ padding: '24px' }}>
                                        <h3 style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            margin: '0 0 20px 0',
                                            fontSize: '18px',
                                            fontWeight: '600'
                                        }}>
                                            <Shield style={{ width: '20px', height: '20px', color: '#dc2626' }} />
                                            Paramètres de sécurité
                                        </h3>

                                        <div style={{ display: 'grid', gap: '24px' }}>
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '16px',
                                                background: '#fef2f2',
                                                borderRadius: '8px',
                                                border: '1px solid #fecaca'
                                            }}>
                                                <div>
                                                    <h4 style={{ margin: '0 0 4px 0', fontSize: '16px' }}>Authentification à deux facteurs</h4>
                                                    <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                                                        Ajouter une couche de sécurité supplémentaire
                                                    </p>
                                                </div>
                                                <ToggleSwitch
                                                    checked={settings.twoFactorAuth || false}
                                                    onChange={(e) => handleInputChange('twoFactorAuth', e.target.checked)}
                                                    color="#dc2626"
                                                />
                                            </div>

                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                                                <div>
                                                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                                                        Timeout de session (min)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min="5"
                                                        max="480"
                                                        value={settings.sessionTimeout || 60}
                                                        onChange={(e) => handleInputChange('sessionTimeout', parseInt(e.target.value))}
                                                        style={{
                                                            width: '100%',
                                                            padding: '12px',
                                                            border: '1px solid #d1d5db',
                                                            borderRadius: '8px',
                                                            fontSize: '14px',
                                                            boxSizing: 'border-box'
                                                        }}
                                                    />
                                                </div>

                                                <div>
                                                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                                                        Expiration mot de passe (jours)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min="30"
                                                        max="365"
                                                        value={settings.passwordExpiry || 90}
                                                        onChange={(e) => handleInputChange('passwordExpiry', parseInt(e.target.value))}
                                                        style={{
                                                            width: '100%',
                                                            padding: '12px',
                                                            border: '1px solid #d1d5db',
                                                            borderRadius: '8px',
                                                            fontSize: '14px',
                                                            boxSizing: 'border-box'
                                                        }}
                                                    />
                                                </div>

                                                <div>
                                                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                                                        Max tentatives de connexion
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min="3"
                                                        max="10"
                                                        value={settings.loginAttempts || 5}
                                                        onChange={(e) => handleInputChange('loginAttempts', parseInt(e.target.value))}
                                                        style={{
                                                            width: '100%',
                                                            padding: '12px',
                                                            border: '1px solid #d1d5db',
                                                            borderRadius: '8px',
                                                            fontSize: '14px',
                                                            boxSizing: 'border-box'
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* System Tab */}
                                {activeTab === 'system' && (
                                    <div style={{ display: 'grid', gap: '24px' }}>
                                        <div className="category-card" style={{ padding: '24px' }}>
                                            <h3 style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px',
                                                margin: '0 0 20px 0',
                                                fontSize: '18px',
                                                fontWeight: '600'
                                            }}>
                                                <Database style={{ width: '20px', height: '20px', color: '#7c3aed' }} />
                                                Configuration système
                                            </h3>

                                            <div style={{ display: 'grid', gap: '24px' }}>
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    padding: '16px',
                                                    background: '#f3f4f6',
                                                    borderRadius: '8px'
                                                }}>
                                                    <div>
                                                        <h4 style={{ margin: '0 0 4px 0', fontSize: '16px' }}>Sauvegarde automatique</h4>
                                                        <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                                                            Sauvegarde automatique des données
                                                        </p>
                                                    </div>
                                                    <ToggleSwitch
                                                        checked={settings.autoBackup || false}
                                                        onChange={(e) => handleInputChange('autoBackup', e.target.checked)}
                                                        color="#7c3aed"
                                                    />
                                                </div>

                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                                    <div>
                                                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                                                            Fréquence de sauvegarde
                                                        </label>
                                                        <select
                                                            value={settings.backupFrequency || 'daily'}
                                                            onChange={(e) => handleInputChange('backupFrequency', e.target.value)}
                                                            style={{
                                                                width: '100%',
                                                                padding: '12px',
                                                                border: '1px solid #d1d5db',
                                                                borderRadius: '8px',
                                                                fontSize: '14px',
                                                                boxSizing: 'border-box'
                                                            }}
                                                        >
                                                            <option value="hourly">Toutes les heures</option>
                                                            <option value="daily">Quotidienne</option>
                                                            <option value="weekly">Hebdomadaire</option>
                                                            <option value="monthly">Mensuelle</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    padding: '16px',
                                                    background: settings.maintenanceMode ? '#fef2f2' : '#fef3c7',
                                                    borderRadius: '8px',
                                                    border: `1px solid ${settings.maintenanceMode ? '#ef4444' : '#fbbf24'}`
                                                }}>
                                                    <div>
                                                        <h4 style={{ margin: '0 0 4px 0', fontSize: '16px' }}>Mode maintenance</h4>
                                                        <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                                                            {settings.maintenanceMode ? '🔒 Site en maintenance' : '⚠️ Bloque l\'accès pour les utilisateurs'}
                                                        </p>
                                                    </div>
                                                    <ToggleSwitch
                                                        checked={settings.maintenanceMode || false}
                                                        onChange={(e) => handleMaintenanceToggle(e.target.checked)}
                                                        color={settings.maintenanceMode ? "#ef4444" : "#f59e0b"}
                                                    />
                                                </div>
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    padding: '16px',
                                                    background: '#f0f9ff',
                                                    borderRadius: '8px',
                                                    border: '1px solid #0ea5e9'
                                                }}>
                                                    <div>
                                                        <h4 style={{ margin: '0 0 4px 0', fontSize: '16px' }}>Journal d'audit</h4>
                                                        <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                                                            Enregistrer toutes les actions des utilisateurs
                                                        </p>
                                                    </div>
                                                    <ToggleSwitch
                                                        checked={settings.enableAuditLog || false}
                                                        onChange={(e) => handleInputChange('enableAuditLog', e.target.checked)}
                                                        color="#0ea5e9"
                                                    />
                                                </div>

                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    padding: '16px',
                                                    background: '#f0fdf4',
                                                    borderRadius: '8px',
                                                    border: '1px solid #10b981'
                                                }}>
                                                    <div>
                                                        <h4 style={{ margin: '0 0 4px 0', fontSize: '16px' }}>Accès API</h4>
                                                        <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                                                            Permettre l'accès via API REST
                                                        </p>
                                                    </div>
                                                    <ToggleSwitch
                                                        checked={settings.enableApiAccess || false}
                                                        onChange={(e) => handleInputChange('enableApiAccess', e.target.checked)}
                                                        color="#10b981"
                                                    />
                                                </div>

                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    padding: '16px',
                                                    background: '#fef2f2',
                                                    borderRadius: '8px',
                                                    border: '1px solid #ef4444'
                                                }}>
                                                    <div>
                                                        <h4 style={{ margin: '0 0 4px 0', fontSize: '16px' }}>Accès invité</h4>
                                                        <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                                                            Permettre l'accès sans compte utilisateur
                                                        </p>
                                                    </div>
                                                    <ToggleSwitch
                                                        checked={settings.allowGuestAccess || false}
                                                        onChange={(e) => handleInputChange('allowGuestAccess', e.target.checked)}
                                                        color="#ef4444"
                                                    />
                                                </div>

                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                                    <div>
                                                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                                                            Rôle utilisateur par défaut
                                                        </label>
                                                        <select
                                                            value={settings.defaultUserRole || 'user'}
                                                            onChange={(e) => handleInputChange('defaultUserRole', e.target.value)}
                                                            style={{
                                                                width: '100%',
                                                                padding: '12px',
                                                                border: '1px solid #d1d5db',
                                                                borderRadius: '8px',
                                                                fontSize: '14px',
                                                                boxSizing: 'border-box'
                                                            }}
                                                        >
                                                            <option value="user">Utilisateur</option>
                                                            <option value="moderator">Modérateur</option>
                                                            <option value="admin">Administrateur</option>
                                                        </select>
                                                    </div>

                                                    <div>
                                                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                                                            Rétention des données (jours)
                                                        </label>
                                                        <input
                                                            type="number"
                                                            min="30"
                                                            max="3650"
                                                            value={settings.dataRetentionDays || 365}
                                                            onChange={(e) => handleInputChange('dataRetentionDays', parseInt(e.target.value))}
                                                            style={{
                                                                width: '100%',
                                                                padding: '12px',
                                                                border: '1px solid #d1d5db',
                                                                borderRadius: '8px',
                                                                fontSize: '14px',
                                                                boxSizing: 'border-box'
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {/* Advanced Tab */}
                                {activeTab === 'advanced' && (
                                    <div style={{ display: 'grid', gap: '24px' }}>
                                        <div className="category-card" style={{ padding: '24px' }}>
                                            <h3 style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px',
                                                margin: '0 0 20px 0',
                                                fontSize: '18px',
                                                fontWeight: '600'
                                            }}>
                                                <Zap style={{ width: '20px', height: '20px', color: '#f59e0b' }} />
                                                Paramètres avancés
                                            </h3>

                                            <div style={{ display: 'grid', gap: '24px' }}>
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                                    <div>
                                                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                                                            Taille max upload (MB)
                                                        </label>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            max="100"
                                                            value={settings.maxFileUploadSize || 10}
                                                            onChange={(e) => handleInputChange('maxFileUploadSize', parseInt(e.target.value))}
                                                            style={{
                                                                width: '100%',
                                                                padding: '12px',
                                                                border: '1px solid #d1d5db',
                                                                borderRadius: '8px',
                                                                fontSize: '14px',
                                                                boxSizing: 'border-box'
                                                            }}
                                                        />
                                                    </div>

                                                    <div>
                                                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                                                            Éléments par page
                                                        </label>
                                                        <select
                                                            value={settings.defaultItemsPerPage || 20}
                                                            onChange={(e) => handleInputChange('defaultItemsPerPage', parseInt(e.target.value))}
                                                            style={{
                                                                width: '100%',
                                                                padding: '12px',
                                                                border: '1px solid #d1d5db',
                                                                borderRadius: '8px',
                                                                fontSize: '14px',
                                                                boxSizing: 'border-box'
                                                            }}
                                                        >
                                                            <option value={10}>10</option>
                                                            <option value={20}>20</option>
                                                            <option value={50}>50</option>
                                                            <option value={100}>100</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    padding: '16px',
                                                    background: '#fef3c7',
                                                    borderRadius: '8px',
                                                    border: '1px solid #f59e0b'
                                                }}>
                                                    <div>
                                                        <h4 style={{ margin: '0 0 4px 0', fontSize: '16px' }}>Vérification email</h4>
                                                        <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                                                            Exiger la vérification des emails
                                                        </p>
                                                    </div>
                                                    <ToggleSwitch
                                                        checked={settings.enableEmailVerification || false}
                                                        onChange={(e) => handleInputChange('enableEmailVerification', e.target.checked)}
                                                        color="#f59e0b"
                                                    />
                                                </div>

                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    padding: '16px',
                                                    background: '#f0f9ff',
                                                    borderRadius: '8px',
                                                    border: '1px solid #0ea5e9'
                                                }}>
                                                    <div>
                                                        <h4 style={{ margin: '0 0 4px 0', fontSize: '16px' }}>Support chat</h4>
                                                        <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                                                            Activer le support chat en direct
                                                        </p>
                                                    </div>
                                                    <ToggleSwitch
                                                        checked={settings.enableChatSupport || false}
                                                        onChange={(e) => handleInputChange('enableChatSupport', e.target.checked)}
                                                        color="#0ea5e9"
                                                    />
                                                </div>

                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                                    <div>
                                                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                                                            Longueur min. mot de passe
                                                        </label>
                                                        <input
                                                            type="number"
                                                            min="6"
                                                            max="20"
                                                            value={settings.passwordMinLength || 8}
                                                            onChange={(e) => handleInputChange('passwordMinLength', parseInt(e.target.value))}
                                                            style={{
                                                                width: '100%',
                                                                padding: '12px',
                                                                border: '1px solid #d1d5db',
                                                                borderRadius: '8px',
                                                                fontSize: '14px',
                                                                boxSizing: 'border-box'
                                                            }}
                                                        />
                                                    </div>

                                                    <div>
                                                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                                                            Limite API (req/min)
                                                        </label>
                                                        <input
                                                            type="number"
                                                            min="10"
                                                            max="1000"
                                                            value={settings.apiRateLimit || 100}
                                                            onChange={(e) => handleInputChange('apiRateLimit', parseInt(e.target.value))}
                                                            style={{
                                                                width: '100%',
                                                                padding: '12px',
                                                                border: '1px solid #d1d5db',
                                                                borderRadius: '8px',
                                                                fontSize: '14px',
                                                                boxSizing: 'border-box'
                                                            }}
                                                        />
                                                    </div>
                                                </div>

                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    padding: '16px',
                                                    background: '#fef2f2',
                                                    borderRadius: '8px',
                                                    border: '1px solid #ef4444'
                                                }}>
                                                    <div>
                                                        <h4 style={{ margin: '0 0 4px 0', fontSize: '16px' }}>Caractères spéciaux requis</h4>
                                                        <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                                                            Exiger des caractères spéciaux dans les mots de passe
                                                        </p>
                                                    </div>
                                                    <ToggleSwitch
                                                        checked={settings.requirePasswordSpecialChars || false}
                                                        onChange={(e) => handleInputChange('requirePasswordSpecialChars', e.target.checked)}
                                                        color="#ef4444"
                                                    />
                                                </div>

                                                <div>
                                                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                                                        Google Analytics ID
                                                    </label>
                                                    <input
                                                        type="text"
                                                        placeholder="G-XXXXXXXXXX"
                                                        value={settings.googleAnalyticsId || ''}
                                                        onChange={(e) => handleInputChange('googleAnalyticsId', e.target.value)}
                                                        style={{
                                                            width: '100%',
                                                            padding: '12px',
                                                            border: '1px solid #d1d5db',
                                                            borderRadius: '8px',
                                                            fontSize: '14px',
                                                            boxSizing: 'border-box'
                                                        }}
                                                    />
                                                </div>

                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                                    <div>
                                                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                                                            Heure début travail
                                                        </label>
                                                        <input
                                                            type="time"
                                                            value={settings.workingHours?.start || '09:00'}
                                                            onChange={(e) => handleInputChange('workingHours', {
                                                                ...settings.workingHours,
                                                                start: e.target.value
                                                            })}
                                                            style={{
                                                                width: '100%',
                                                                padding: '12px',
                                                                border: '1px solid #d1d5db',
                                                                borderRadius: '8px',
                                                                fontSize: '14px',
                                                                boxSizing: 'border-box'
                                                            }}
                                                        />
                                                    </div>

                                                    <div>
                                                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                                                            Heure fin travail
                                                        </label>
                                                        <input
                                                            type="time"
                                                            value={settings.workingHours?.end || '17:00'}
                                                            onChange={(e) => handleInputChange('workingHours', {
                                                                ...settings.workingHours,
                                                                end: e.target.value
                                                            })}
                                                            style={{
                                                                width: '100%',
                                                                padding: '12px',
                                                                border: '1px solid #d1d5db',
                                                                borderRadius: '8px',
                                                                fontSize: '14px',
                                                                boxSizing: 'border-box'
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {/* Appearance Tab */}
                                {activeTab === 'appearance' && (
                                    <div className="category-card" style={{ padding: '24px' }}>
                                        <h3 style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            margin: '0 0 20px 0',
                                            fontSize: '18px',
                                            fontWeight: '600'
                                        }}>
                                            <Palette style={{ width: '20px', height: '20px', color: '#ec4899' }} />
                                            Apparence et interface
                                        </h3>

                                        <div style={{ display: 'grid', gap: '24px' }}>
                                            <div>
                                                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '12px' }}>
                                                    Thème
                                                </label>
                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                                                    {['light', 'dark', 'auto'].map((theme) => (
                                                        <button
                                                            key={theme}
                                                            onClick={() => handleInputChange('theme', theme)}
                                                            style={{
                                                                padding: '16px',
                                                                border: settings.theme === theme ? '2px solid #1e40af' : '2px solid #e5e7eb',
                                                                borderRadius: '8px',
                                                                background: settings.theme === theme ? '#eff6ff' : 'white',
                                                                cursor: 'pointer',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'center',
                                                                gap: '8px'
                                                            }}
                                                        >
                                                            {theme === 'light' && <Sun style={{ width: '24px', height: '24px' }} />}
                                                            {theme === 'dark' && <Moon style={{ width: '24px', height: '24px' }} />}
                                                            {theme === 'auto' && <Laptop style={{ width: '24px', height: '24px' }} />}
                                                            <span style={{ fontSize: '14px', fontWeight: '500' }}>
                                                                {theme === 'light' ? 'Clair' : theme === 'dark' ? 'Sombre' : 'Auto'}
                                                            </span>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '16px',
                                                background: '#fef7ff',
                                                borderRadius: '8px',
                                                border: '1px solid #ec4899'
                                            }}>
                                                <div>
                                                    <h4 style={{ margin: '0 0 4px 0', fontSize: '16px' }}>Mode compact</h4>
                                                    <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                                                        Interface plus dense avec moins d'espacement
                                                    </p>
                                                </div>
                                                <ToggleSwitch
                                                    checked={settings.compactMode || false}
                                                    onChange={(e) => handleInputChange('compactMode', e.target.checked)}
                                                    color="#ec4899"
                                                />
                                            </div>

                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '16px',
                                                background: '#f9fafb',
                                                borderRadius: '8px'
                                            }}>
                                                <div>
                                                    <h4 style={{ margin: '0 0 4px 0', fontSize: '16px' }}>Animations</h4>
                                                    <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                                                        Activer les transitions et animations
                                                    </p>
                                                </div>
                                                <ToggleSwitch
                                                    checked={settings.showAnimations || false}
                                                    onChange={(e) => handleInputChange('showAnimations', e.target.checked)}
                                                    color="#1e40af"
                                                />
                                            </div>

                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '16px',
                                                background: '#f9fafb',
                                                borderRadius: '8px'
                                            }}>
                                                <div>
                                                    <h4 style={{ margin: '0 0 4px 0', fontSize: '16px' }}>Sidebar réduite par défaut</h4>
                                                    <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                                                        Démarrer avec la barre latérale réduite
                                                    </p>
                                                </div>
                                                <ToggleSwitch
                                                    checked={settings.sidebarCollapsed || false}
                                                    onChange={(e) => handleInputChange('sidebarCollapsed', e.target.checked)}
                                                    color="#6366f1"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </main>
        </div>
    );
};

export default UnitechSettingsPage;